import { UniversesQueryResult } from "@/cms/sanity.types";
import { FacetConfig, HIERARCHY_SEPARATOR, STANDARD_FACETS } from "./constants";
import { Filters } from "./search";
import { filterNullArrays } from "@cappelendamm/utils/array";
import { groupBy, sortBy } from "lodash";

export function getSearchFilterByObjectIDs(objectIDs: string[]): string {
  return `(${objectIDs.map((value) => `objectID = ${JSON.stringify(value)}`).join(" OR ")})`;
}

export function getShopFilterFromUniverse<
  B extends Pick<NonNullable<UniversesQueryResult[number]>, "shop">,
>(universe: B | null) {
  return universe?.shop?.current
    ? `shops.path = "${universe.shop.current}"`
    : "";
}

export function getFilterFromHierarchyFacet(
  values: string[],
  facet: string,
  separator: "/" = HIERARCHY_SEPARATOR,
): string[] {
  const baseAttribute = facet.split(".lvl").at(0) || facet;

  // Group by root category
  const grouped = groupBy(values, (value) => value.split(separator)[0]);

  // For each root, keep only the values at the maximum depth
  const filters = Object.values(grouped).flatMap((group) => {
    const maxDepth = Math.max(...group.map((p) => p.split(separator).length));
    return group
      .filter((p) => p.split(separator).length === maxDepth)
      .map((path) => ({
        level: maxDepth - 1,
        filter: `${baseAttribute}.lvl${maxDepth - 1} = "${path}"`,
      }));
  });

  return sortBy(filters, (f) => f.level).map((f) => f.filter);
}

export function getFilterFromSearchParams(
  searchParams: URLSearchParams,
  facets: readonly FacetConfig[] | undefined = STANDARD_FACETS,
): Filters {
  const filters: Filters = [];
  const processedKeys = new Set<string>();

  for (const key of searchParams.keys()) {
    if (processedKeys.has(key)) continue;

    const facet = facets.find(
      (f) => f.attribute === key || f.queryParam === key,
    );
    if (!facet) continue;

    processedKeys.add(key);
    const values = searchParams.getAll(key);

    if (values.length > 0) {
      const isHierarchy = facet.type === "hierarchy";

      const filter = isHierarchy
        ? getFilterFromHierarchyFacet(values, facet.attribute)
        : values.map((v) => `${facet.attribute} = "${v}"`);

      if (filter && facet.operator === "or") filters.push(filter);
      if (filter && facet.operator === "and") filters.push(...filter);
    }
  }

  return filters;
}

export function getHierarchyFacetsFromFilters(
  filters: Filters | undefined,
  attribute: string,
) {
  if (!filters) return [`${attribute}.lvl0`];
  const baseAttribute = attribute.split(".lvl").at(0)!;
  const levels = filters
    .flat()
    .map((f) => f.match(new RegExp(`${baseAttribute}\\.lvl(\\d+)`)))
    .filter((match) => !!match)
    .map(([_, level]) => Number(level));
  const maxLevel = levels.length ? Math.max(...levels) : -1;
  if (maxLevel === -1) return [`${baseAttribute}.lvl0`];
  return Array.from(
    { length: maxLevel + 2 },
    (_, i) => `${baseAttribute}.lvl${i}`,
  );
}

export function getFacetFromFacetConfig(
  facetConfig: FacetConfig[] = STANDARD_FACETS,
  filters?: Filters,
) {
  if (!facetConfig) return [];

  return facetConfig.flatMap((facet) => {
    if (facet.type === "hierarchy")
      return getHierarchyFacetsFromFilters(filters, facet.attribute);
    return facet.attribute;
  });
}

export function removeFilterByAttribute(
  filters: Filters | undefined,
  attribute: string,
): Filters {
  if (!filters) return [];

  return filterNullArrays(
    filters.map((filter) => {
      if (typeof filter === "string") {
        return filter.includes(attribute) ? null : filter;
      }
      const filtered = filter.filter((filter) => !filter.includes(attribute));
      if (filtered.length === 0) return null;
      return filtered;
    }),
  );
}
